<template>
  <RevSkeleton v-if="pending" height="318px" shape="rectangle" width="100%" />

  <RevCard v-else-if="summary" class="p-16" data-test="rating">
    <h3 class="heading-3 mb-4">{{ i18n(translations.title) }}</h3>
    <RevRating
      :score="rating.averageRate"
      :size="isTablet ? 'small' : 'medium'"
    />
    <span
      class="text-static-info-hi body-2-bold mb-20 mt-4 flex w-fit max-w-full items-start truncate py-0 pl-2 pr-4"
    >
      <IconQuality class="mr-4 h-20 w-20 min-w-20" size="16" />

      <span class="text-wrap">
        {{ i18n(translations.reviews, { count: rating.count }) }}
      </span>
    </span>
    <h3 id="reviews-details-summary-rating-details" class="heading-3">
      {{ i18n(translations.ratingDetails) }}
    </h3>
    <div class="relative">
      <ul
        id="reviews-details-list"
        class="mt-4 line-clamp-1 overflow-hidden text-ellipsis"
        :style="{
          maxHeight: expanded ? 'none' : '72px',
        }"
      >
        <li v-for="detail in summary || []" :key="detail.identifier">
          <div class="mt-4 flex items-center justify-between">
            <p class="text-static-default-low body-2 mr-8 break-words">
              {{
                i18n(
                  detailTitleByIdentifier[
                    detail.identifier as ProductRatingDetailsIdentifierWithoutAttachments
                  ],
                )
              }}
            </p>
            <p class="text-static-default-hi body-2-bold">
              {{
                i18n.number(detail.averageRate, { maximumFractionDigits: 1 })
              }}/{{ MAX_RATING }}
            </p>
          </div>
        </li>
      </ul>

      <!-- Gradient overlay -->
      <div
        v-if="displaySeeMoreButton && !expanded"
        class="!to-bg-surface-default-low pointer-events-none absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-b from-transparent"
      />
    </div>

    <RevButton
      v-if="displaySeeMoreButton"
      :id="`reviews-details-summary-button-${expanded ? 'see-less' : 'see-more'}`"
      aria-controls="reviews-details-list"
      :aria-expanded="expanded"
      :aria-labelledby="`reviews-details-summary-button-${expanded ? 'see-less' : 'see-more'} reviews-details-summary-rating-details`"
      class="mt-8"
      fullWidth="always"
      :icon="expanded ? IconChevronUp : IconChevronDown"
      size="small"
      variant="secondary"
      @click="toogle"
    >
      {{ i18n(expanded ? translations.seeLess : translations.seeMore) }}
    </RevButton>

    <LegalModal />
  </RevCard>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'

import type { Rating } from '@backmarket/http-api/src/api-specs-reviews/types/rating'
import type {
  ProductRatingDetailsIdentifierWithoutAttachments,
  ReviewRatingsSummary,
} from '@backmarket/http-api/src/api-specs-reviews/types/review'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { Breakpoint } from '@backmarket/utils/dom/getCurrentBreakpoint'
import type { Maybe } from '@backmarket/utils/types/Maybe'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevRating } from '@ds/components/Rating'
import { RevSkeleton } from '@ds/components/Skeleton'
import { IconChevronDown } from '@ds/icons/IconChevronDown'
import { IconChevronUp } from '@ds/icons/IconChevronUp'
import { IconQuality } from '@ds/icons/IconQuality'

import { MAX_RATING } from '../../constants'

import { detailTitleByIdentifier } from './ReviewsDetailsSummary.constants'
import translations from './ReviewsDetailsSummary.translations'
import LegalModal from './components/LegalModal.vue'

const i18n = useI18n()
const expanded = ref(false)
const breakpoint = useClientOnlyComputedBreakpoint()
const isTablet = computed(
  () => breakpoint.value > Breakpoint.SM && breakpoint.value < Breakpoint.LG,
)

const props = defineProps<{
  rating: Rating
  summary: Maybe<ReviewRatingsSummary>
  pending: boolean
}>()

function toogle() {
  expanded.value = !expanded.value
}

const displaySeeMoreButton = computed(() => {
  return props.summary?.length && props.summary.length > 3
})
</script>
